import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import CalendarPage from './CalendarPage';
import {
  apiGetMyCalendar, apiGetMyCalendarShows, apiGetShows,
  type MyCalendarLink, type MyCalendarShow, type ShowDTO,
} from '../api';
import { useProfileContext } from '../hooks/useProfileContext';

/**
 * PERSONAL CALENDAR (SPEC-PERSONAL-CALENDAR, 2026-10-21) — the /tour/calendar
 * page, READING ONLY:
 *  - shows where the signed-in user is « concerné » (same rule as the personal
 *    daysheet of the checklists V2 — the server computes the filter, this page
 *    only renders it);
 *  - « Add to my calendar » banner: copy the personal .ics link + webcal://
 *    subscription; the link is ALWAYS available here (also in Account);
 *  - editor+ get an All / My events filter (default All); a viewer sees ONLY
 *    his own events;
 *  - clicking an event opens the READ view of the show (DaysheetViewer —
 *    coherent with viewer-surface v2): never ?edit=1 here.
 */
export default function PersonalCalendarPage() {
  const { t } = useTranslation();
  const { activeProfileId, getProfile } = useProfileContext();
  const isViewer = !!activeProfileId && getProfile(activeProfileId)?.my_role === 'viewer';

  const [mine, setMine] = useState<MyCalendarShow[]>([]);
  const [all, setAll] = useState<ShowDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [scopeChoice, setScope] = useState<'all' | 'mine' | null>(null);
  const [link, setLink] = useState<MyCalendarLink | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([apiGetMyCalendarShows(), apiGetShows(), apiGetMyCalendar()])
      .then(([m, a, l]) => { setMine(m.shows || []); setAll(a || []); setLink(l); })
      .catch(() => { /* the banner reflects the missing link; the grid stays empty */ })
      .finally(() => setLoading(false));
  }, []);

  const copyLink = useCallback(async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${link.url}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard refused — the link stays visible in Account */ }
  }, [link]);

  // The scope is DERIVED each render: the band payload (and thus my_role)
  // arrives after mount, so the default must not be frozen in useState —
  // otherwise a viewer briefly renders (and keeps) the « all » scope.
  const scope: 'all' | 'mine' = isViewer ? 'mine' : (scopeChoice ?? 'all');
  const shows = scope === 'mine' ? mine : all;

  const banner = (
    <div
      data-my-calendar-banner
      style={{
        display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center',
        padding: '10px 14px', marginBottom: 16, borderRadius: 8,
        background: 'color-mix(in srgb, var(--text-muted) 8%, transparent)',
        border: '1px solid var(--border)',
      }}
    >
      <strong style={{ fontSize: 'var(--font-sm)' }}>{t('myCalendar.addToMyCalendar')}</strong>
      {link && (
        <>
          <button className="btn btn-secondary" style={{ padding: '3px 10px', fontSize: 'var(--font-sm)' }} onClick={copyLink}>
            {copied ? t('myCalendar.linkCopied') : t('myCalendar.copyLink')}
          </button>
          {/* webcal:// lets Apple/Google/Outlook open the subscribe dialog. */}
          <a
            className="btn btn-secondary"
            style={{ padding: '3px 10px', fontSize: 'var(--font-sm)', textDecoration: 'none' }}
            href={link.webcal_url}
          >
            {t('myCalendar.subscribe')}
          </a>
        </>
      )}
      {!link && !loading && (
        <span style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)' }}>{t('myCalendar.linkUnavailable')}</span>
      )}
      {link && !link.member_linked && (
        <span style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)' }}>{t('myCalendar.linkTeamFirst')}</span>
      )}
      <span style={{ flex: 1 }} />
      {!isViewer && (
        <div style={{ display: 'inline-flex', gap: 4 }}>
          <button
            data-calendar-scope="all"
            className={`btn ${scope === 'all' ? 'btn-primary' : 'btn-secondary'}`}
            style={{ padding: '3px 10px', fontSize: 'var(--font-sm)' }}
            onClick={() => setScope('all')}
          >
            {t('myCalendar.allEvents')}
          </button>
          <button
            data-calendar-scope="mine"
            className={`btn ${scope === 'mine' ? 'btn-primary' : 'btn-secondary'}`}
            style={{ padding: '3px 10px', fontSize: 'var(--font-sm)' }}
            onClick={() => setScope('mine')}
          >
            {t('myCalendar.myEvents')}
          </button>
        </div>
      )}
      <Link to="/account" style={{ fontSize: 'var(--font-sm)', color: 'var(--text-dim)' }}>
        {t('myCalendar.manageInAccount')}
      </Link>
    </div>
  );

  return (
    <CalendarPage
      shows={shows}
      loading={loading}
      title={t('myCalendar.title')}
      banner={banner}
    />
  );
}
